import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Moon, Clock } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const statusChip = {
  pending: "border-gold/40 text-gold bg-gold/5",
  countered: "border-cream/40 text-cream bg-cream/5",
  accepted: "border-emerald-400/40 text-emerald-300 bg-emerald-400/5",
  declined: "border-red-400/40 text-red-300 bg-red-400/5",
  declined_by_member: "border-red-400/40 text-red-300 bg-red-400/5",
};

export default function AfterHoursMine() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/after_hours/mine").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  useEffect(() => { if (user) load(); else setLoading(false); }, [user]);

  const acceptCounter = async (rid) => {
    try {
      const { data } = await api.post(`/after_hours/${rid}/counter_accept`);
      toast.success("Counter accepted — booking created.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not accept counter"); }
  };
  const decline = async (rid) => {
    try {
      await api.post(`/after_hours/${rid}/decline`);
      toast.success("Counter declined.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not decline"); }
  };

  if (!user) return (
    <section className="max-w-md mx-auto px-6 py-24 text-center">
      <p className="overline mb-3">Sign in required</p>
      <Link to="/auth" className="btn-gold inline-block mt-8 px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">Sign in</Link>
    </section>
  );
  if (loading) return <div className="max-w-3xl mx-auto px-5 py-10 text-cream/50">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-24" data-testid="afterhours-mine-page">
      <p className="overline mb-2 flex items-center gap-2 justify-start"><Moon size={12} weight="fill" color="#C5A059" /> After-hours</p>
      <h1 className="font-display text-4xl md:text-5xl tracking-tighter text-cream">Your requests</h1>

      {!items.length && (
        <div className="mt-10 border border-cream/10 p-6 text-center" data-testid="afterhours-mine-empty">
          <p className="text-cream/70">No after-hours requests yet.</p>
          <Link to="/discover" className="btn-gold inline-block mt-6 px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">Discover Studios</Link>
        </div>
      )}

      <div className="mt-8 space-y-4">
        {items.map((r) => (
          <div key={r.id} className="border border-cream/10 p-5" data-testid={`afterhours-item-${r.id}`}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="font-display text-xl text-cream">{r.studio_name}</p>
              <span className={`chip text-[10px] border ${statusChip[r.status] || "border-cream/20"}`}>{r.status.replace(/_/g, " ")}</span>
            </div>
            <p className="text-sm text-cream/80">{r.service_name}</p>
            <p className="text-xs text-cream/60 mt-1 flex items-center gap-2"><Clock size={12} /> Proposed {new Date(r.proposed_time).toLocaleString()}</p>
            {r.counter_time && (
              <p className="text-xs text-gold mt-1 flex items-center gap-2"><Clock size={12} weight="fill" /> Countered {new Date(r.counter_time).toLocaleString()}</p>
            )}
            {r.counter_message && <p className="text-xs text-cream/70 mt-2 italic">"{r.counter_message}"</p>}

            {r.status === "countered" && (
              <div className="mt-4 flex gap-2">
                <button data-testid={`afterhours-counter-accept-${r.id}`} onClick={() => acceptCounter(r.id)} className="btn-gold px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold">Accept counter</button>
                <button data-testid={`afterhours-counter-decline-${r.id}`} onClick={() => decline(r.id)} className="btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em]">Decline</button>
              </div>
            )}

            {r.status === "accepted" && r.booking_id && (
              <Link to={`/upcoming`} data-testid={`afterhours-view-booking-${r.id}`} className="mt-4 inline-block btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em]">View booking</Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
